import type { ReactNode } from "react"

import type { MetricRecord } from "@/metrics/runtime"
import { Skeleton } from "@coval/ui"

import { hashMetricMetadata } from "../utils/experimentStorageKey"
import { AnnotationsView } from "./components/AnnotationsView"
import { RecentTranscriptsView } from "./components/RecentTranscriptsView"
import {
  TEST_DRAWER_VIEW_MODE,
  TestDrawerProvider,
  TestMetricRuntimeProvider,
  useTestDrawer,
  type TEST_DRAWER_TAB_OPTIONS,
} from "./contexts"

const SCOPED_TAB_OPTIONS: typeof TEST_DRAWER_TAB_OPTIONS = [
  {
    key: TEST_DRAWER_VIEW_MODE.ANNOTATIONS,
    title: "With annotations",
  },
  {
    key: TEST_DRAWER_VIEW_MODE.SIMULATIONS,
    title: "Simulations",
  },
]

function ScopedTestMetricDrawerContent({
  annotationSimulationIds,
  headerContent,
  isLoadingScope,
  scopeContent,
  simulationIds,
  experimentStorageScope,
}: {
  annotationSimulationIds?: string[]
  headerContent?: ReactNode
  isLoadingScope: boolean
  scopeContent?: ReactNode
  simulationIds: string[]
  experimentStorageScope?: unknown
}) {
  const { viewMode } = useTestDrawer()
  const isAnnotations = viewMode === TEST_DRAWER_VIEW_MODE.ANNOTATIONS
  const viewKey = hashMetricMetadata(experimentStorageScope)

  return (
    <aside
      aria-label="Test expected behaviors"
      className="relative flex h-full w-1/2 min-w-[30rem] shrink-0 flex-col overflow-hidden rounded-lg border border-coval-border bg-background"
    >
      {isAnnotations ? (
        <AnnotationsView
          key={viewKey}
          headerContent={headerContent}
          headerTitle="Compare expected behaviors"
          scopeContent={scopeContent}
          simulationOutputIds={annotationSimulationIds ?? simulationIds}
          tabOptions={SCOPED_TAB_OPTIONS}
        />
      ) : (
        <RecentTranscriptsView
          key={viewKey}
          allowImport={false}
          headerContent={headerContent}
          headerTitle="Compare expected behaviors"
          scopeContent={scopeContent}
          monitoringFilter={false}
          tabOptions={SCOPED_TAB_OPTIONS}
        />
      )}
      {isAnnotations && isLoadingScope && (
        <div className="absolute inset-x-0 bottom-0 top-16 z-20 flex items-center justify-center bg-background/90">
          <Skeleton className="h-4 w-32 rounded-md" />
        </div>
      )}
    </aside>
  )
}

export function ScopedTestMetricDrawer({
  headerContent,
  isLoadingScope,
  metric,
  onClose,
  simulationIds,
  annotationSimulationIds,
  criteriaOverride,
  experimentStorageScope,
  beforeRun,
  onRunningChange,
  scopeContent,
}: {
  headerContent?: ReactNode
  isLoadingScope: boolean
  metric: MetricRecord
  onClose: () => void
  simulationIds: string[]
  annotationSimulationIds?: string[]
  criteriaOverride?: string[]
  experimentStorageScope?: unknown
  beforeRun?: () => Promise<void>
  onRunningChange?: (isRunning: boolean) => void
  scopeContent?: ReactNode
}) {
  return (
    <TestMetricRuntimeProvider
      metric={metric}
      simulationIds={simulationIds}
      annotationSimulationIds={annotationSimulationIds}
      criteriaFirst
      criteriaOverride={criteriaOverride}
      experimentStorageScope={experimentStorageScope}
      beforeRun={beforeRun}
      onRunningChange={onRunningChange}
    >
      <TestDrawerProvider
        defaultViewMode={TEST_DRAWER_VIEW_MODE.ANNOTATIONS}
        initiallyOpen
        onClose={onClose}
      >
        <ScopedTestMetricDrawerContent
          annotationSimulationIds={annotationSimulationIds}
          headerContent={headerContent}
          isLoadingScope={isLoadingScope}
          scopeContent={scopeContent}
          simulationIds={simulationIds}
          experimentStorageScope={experimentStorageScope}
        />
      </TestDrawerProvider>
    </TestMetricRuntimeProvider>
  )
}
